import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { ApiService, ExcelExportExtendedModel, FundMeasurementDateName } from 'api';
import { COMPLETED_VALUE } from 'layouts/Main/components/ExcelExportDialogV2/constants';
import { usePollingByInterval } from 'services/hooks';
import { isValidDate, Logger } from 'utilities';

const STATUS_POLLING_INTERVAL_MS = 10000;

const useCompanyReportsStatuses = (
  firmId: number | null,
  measurementDate: FundMeasurementDateName | null,
  componentIsVisible: boolean
) => {
  const { enqueueSnackbar } = useSnackbar();
  const [statusPollingInterval, setStatusPollingInterval] = useState<number | null>(null);
  const [reportStatuses, setReportStatuses] = useState<ExcelExportExtendedModel[] | null>(null);
  const [reportsToRequest, setReportsToRequest] = useState({} as Record<number, boolean>); // these will be included in the zip job
  const [enqueueToTrigger, setEnqueueToTrigger] = useState<Set<number>>(new Set()); // these will be enqueued for generation so they can be included in the zip job later

  const queryDate = useMemo(() => {
    if (measurementDate && isValidDate(measurementDate.date)) {
      return measurementDate.date;
    }
    return null;
  }, [measurementDate]);

  const reportStatusesMap = useMemo(
    () =>
      (reportStatuses ?? []).reduce(
        (accumulator, rs) => {
          accumulator[rs.company_measurement_date] = rs.status!;
          return accumulator;
        },
        {} as Record<number, number>
      ),
    [reportStatuses]
  );

  const retrieveFullCompanyReportsStatuses = useCallback(async () => {
    try {
      return await ApiService.apiExcelExportsFullCompanyReportsByDateRead(firmId!.toString(), queryDate!);
    } catch (error) {
      Logger.error('Error in retrieveFullCompanyReportsStatuses API call', error);
      setStatusPollingInterval(null);
      enqueueSnackbar('Error retrieving full company reports statuses.', { variant: 'error' });
      return null;
    }
  }, [enqueueSnackbar, firmId, queryDate, setStatusPollingInterval]);

  const handleIndividualChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setReportsToRequest(prevState => ({
        ...prevState,
        [Number(event.target.id)]: event.target.checked,
      }));
    },
    [setReportsToRequest]
  );

  const handleEnqueueCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEnqueueToTrigger(prevSet => {
        // prevSet is truthy when user has already interacted with the enqueue checkboxes
        const newSet = new Set(prevSet);
        const id = Number(event.target.id);
        if (event.target.checked) {
          newSet.add(id);
        } else {
          newSet.delete(id);
        }
        return newSet;
      });
    },
    [setEnqueueToTrigger]
  );

  const handleGlobalToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (reportsToRequest && reportStatusesMap) {
        // need to skip the indeterminates by checking reportStatusesMap
        setReportsToRequest(() =>
          Object.keys(reportsToRequest).reduce(
            (acc, key) => {
              acc[Number(key)] = Boolean(event.target.checked && reportStatusesMap[Number(key)] === COMPLETED_VALUE);
              return acc;
            },
            {} as Record<number, boolean>
          )
        );
      }
    },
    [reportsToRequest, reportStatusesMap]
  );

  const refreshStatuses = useCallback(async () => {
    const updatedStatuses = await retrieveFullCompanyReportsStatuses();
    if (updatedStatuses) {
      setReportStatuses(updatedStatuses);
    }
  }, [setReportStatuses, retrieveFullCompanyReportsStatuses]);

  useEffect(() => {
    if (firmId && queryDate) {
      refreshStatuses();
    }
  }, [firmId, queryDate, refreshStatuses]);

  useEffect(() => {
    if (reportStatuses) {
      const initialReportsToRequest = reportStatuses.reduce(
        (acc, rs) => {
          if (rs.company_measurement_date) {
            acc[rs.company_measurement_date] = rs.status === COMPLETED_VALUE;
          }
          return acc;
        },
        {} as Record<number, boolean>
      );
      setReportsToRequest(initialReportsToRequest);
    }
  }, [reportStatuses, setReportsToRequest]);

  // poll statuses only when component is visible and a first retrieval has happened
  useEffect(() => {
    if (componentIsVisible && reportStatuses) {
      setStatusPollingInterval(STATUS_POLLING_INTERVAL_MS);
    } else {
      setStatusPollingInterval(null);
    }
  }, [componentIsVisible, reportStatuses]);

  usePollingByInterval(refreshStatuses, statusPollingInterval);

  return {
    retrieveFullCompanyReportsStatuses,
    reportStatuses,
    reportStatusesMap,
    reportsToRequest,
    handleIndividualChange,
    handleGlobalToggle,
    setStatusPollingInterval,
    handleEnqueueCheckboxChange,
    enqueueToTrigger,
    setEnqueueToTrigger,
  };
};

export default useCompanyReportsStatuses;
